import { createInterruptibleStorage, useCookiePreferencesStore } from "src/features/cookie-policy";
import { v4 as uuidV4 } from "uuid";
import { create } from "zustand";
import { combine, persist, createJSONStorage } from "zustand/middleware";

import { jwtDecode } from "@sol/sdk/utils/jwt";

export const AUTHENTICATION_KEY = "sol:authentication";

export enum LogoutReason {
    TokenExpired = "token_expired",
}

const getAuthenticatedTokenState = (token: string, refreshToken: string) => ({
    token,
    refreshToken,
    userId: jwtDecode(token).sub ?? null,
    logoutReason: null,
});

const DEFAULT_STATE: {
    sessionId: string | null;
    token: string | null;
    refreshToken: string | null;
    userId: string | null;
    logoutReason: string | null;
} = {
    sessionId: null,
    token: null,
    refreshToken: null,
    userId: null,
    logoutReason: null,
};

export const useAuthenticationStore = create(
    persist(
        combine(DEFAULT_STATE, (set, get) => ({
            login: (token: string, refreshToken: string) =>
                set(() => ({
                    ...getAuthenticatedTokenState(token, refreshToken),
                    sessionId: uuidV4(),
                })),
            // Token rotation keeps the current logical session.
            rotateTokens: (token: string, refreshToken: string) =>
                set(() => getAuthenticatedTokenState(token, refreshToken)),
            logout: (reason: LogoutReason | null = null) =>
                set(() => ({
                    sessionId: null,
                    token: null,
                    refreshToken: null,
                    userId: null,
                    logoutReason: reason,
                })),
            tokenInfo: () => {
                const { token } = get();
                if (token) {
                    return jwtDecode(token);
                }

                return null;
            },
            clearLogoutReason: () => set(() => ({ logoutReason: null })),
        })),
        {
            name: AUTHENTICATION_KEY,
            storage: createInterruptibleStorage(() => createJSONStorage<typeof DEFAULT_STATE>(() => localStorage, {}), {
                interrupt: name => {
                    if (name === AUTHENTICATION_KEY) {
                        return !useCookiePreferencesStore.getState().isPolicyAccepted;
                    }
                    return false;
                },
            }),
            partialize: state => ({ ...state, logoutReason: null }), // Do not persist logoutReason
        },
    ),
);
